import React, { useState, useEffect } from 'react';
import { contentAPI } from '../../services/api';

const SettingsSection = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await contentAPI.getSettings();
      const settingsData = response.data || response || [];
      
      const settingsMap = {};
      settingsData.forEach(setting => {
        settingsMap[setting.setting_key] = {
          value: setting.setting_value || '',
          value_ar: setting.setting_value_ar || ''
        };
      });
      
      setSettings(settingsMap);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key) => {
    try {
      setSaving(true);
      await contentAPI.updateSetting({
        setting_key: key,
        setting_value: settings[key]?.value || '',
        setting_value_ar: settings[key]?.value_ar || ''
      });
      alert('✅ Paramètre enregistré!');
    } catch (error) {
      alert('❌ Erreur lors de l\'enregistrement');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, field, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement des paramètres...</p>
      </div>
    );
  }

  const settingsGroups = [
    {
      title: 'Informations de la Ligue',
      icon: '🏆',
      settings: [
        { key: 'league_name', label: 'Nom de la ligue', labelAr: 'اسم الرابطة' },
        { key: 'contact_email', label: 'Email de contact', labelAr: 'البريد الإلكتروني' },
        { key: 'contact_phone', label: 'Téléphone', labelAr: 'الهاتف' },
        { key: 'contact_address', label: 'Adresse', labelAr: 'العنوان' }
      ]
    },
    {
      title: 'Paramètres des Résultats',
      icon: '📊',
      settings: [
        { key: 'results_title', label: 'Titre des résultats', labelAr: 'عنوان النتائج' },
        { key: 'results_subtitle', label: 'Sous-titre des résultats', labelAr: 'العنوان الفرعي للنتائج' },
        { key: 'current_season', label: 'Saison actuelle', labelAr: 'الموسم الحالي' },
        { key: 'match_schedule', label: 'Horaire des matchs', labelAr: 'جدول المباريات' }
      ]
    },
    {
      title: 'Réseaux Sociaux',
      icon: '📱',
      settings: [
        { key: 'facebook_url', label: 'Facebook URL', labelAr: 'رابط فيسبوك' },
        { key: 'twitter_url', label: 'Twitter URL', labelAr: 'رابط تويتر' },
        { key: 'instagram_url', label: 'Instagram URL', labelAr: 'رابط انستغرام' },
        { key: 'youtube_url', label: 'YouTube URL', labelAr: 'رابط يوتيوب' }
      ]
    }
  ];

  return (
    <div>
      <h1 style={styles.title}>⚙️ Paramètres du Site / إعدادات الموقع</h1>
      
      <div style={styles.infoBox}>
        <p>💡 Modifiez les textes et informations affichés sur le site public.</p>
        <p>Ces paramètres sont chargés dynamiquement sur toutes les pages.</p>
      </div>

      {settingsGroups.map((group, idx) => (
        <div key={idx} style={styles.card}>
          <h2 style={styles.cardTitle}>
            <span style={styles.icon}>{group.icon}</span>
            {group.title}
          </h2>
          
          {group.settings.map(setting => (
            <div key={setting.key} style={styles.settingGroup}>
              <div style={styles.settingHeader}>
                <span style={styles.settingLabel}>{setting.label}</span>
                <span style={styles.settingLabelAr}>{setting.labelAr}</span>
              </div>
              
              <div style={styles.inputRow}>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Français</label>
                  <input
                    type="text"
                    value={settings[setting.key]?.value || ''}
                    onChange={(e) => updateSetting(setting.key, 'value', e.target.value)}
                    style={styles.input}
                    placeholder={setting.label}
                  />
                </div>
                
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>العربية</label>
                  <input
                    type="text"
                    value={settings[setting.key]?.value_ar || ''}
                    onChange={(e) => updateSetting(setting.key, 'value_ar', e.target.value)}
                    style={{...styles.input, direction: 'rtl'}}
                    placeholder={setting.labelAr}
                  />
                </div>
              </div>
              
              <button
                onClick={() => handleSave(setting.key)}
                disabled={saving}
                style={styles.saveBtn}
              >
                {saving ? 'Enregistrement...' : '💾 Enregistrer'}
              </button>
            </div>
          ))}
        </div>
      ))}

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>
          <span style={styles.icon}>🔒</span>
          Sécurité
        </h2>
        <div style={styles.securityInfo}>
          <p>⚠️ Pour changer le mot de passe administrateur, contactez le développeur.</p>
          <p>Le mot de passe est crypté dans la base de données pour votre sécurité.</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: '#333'
  },
  infoBox: {
    background: '#e3f2fd',
    border: '2px solid #2196f3',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '30px',
    color: '#1565c0'
  },
  card: {
    background: 'white',
    padding: '30px',
    borderRadius: '15px',
    marginBottom: '30px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#333',
    borderBottom: '2px solid #f0f0f0',
    paddingBottom: '15px'
  },
  icon: {
    fontSize: '28px'
  },
  settingGroup: {
    marginBottom: '30px',
    padding: '20px',
    background: '#f9f9f9',
    borderRadius: '10px',
    border: '1px solid #e0e0e0'
  },
  settingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  settingLabel: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#555'
  },
  settingLabelAr: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#555',
    direction: 'rtl'
  },
  inputRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '15px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  inputLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666'
  },
  input: {
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    transition: 'border-color 0.3s'
  },
  saveBtn: {
    padding: '12px 30px',
    background: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background 0.3s'
  },
  securityInfo: {
    padding: '20px',
    background: '#fff3cd',
    border: '2px solid #ffc107',
    borderRadius: '10px',
    color: '#856404'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#666'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #c41e3a',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus {
    outline: none;
    border-color: #c41e3a !important;
  }
  
  button:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
if (!document.getElementById('settings-section-styles')) {
  styleSheet.id = 'settings-section-styles';
  document.head.appendChild(styleSheet);
}

export default SettingsSection;
